import { Injectable, inject, signal, computed, OnDestroy } from '@angular/core';
import { Observable, Subject, firstValueFrom, takeUntil } from 'rxjs';
import { ApiError } from '../../abstract/api-error';
import { BaseService } from '../../abstract/base.service';
import { WebSocketService } from '../services/websocket.service';
import { StateReport } from '../data-models/state-report.model';
import { StateReportStatus } from '../data-models/state-report-status.model';
import { CreateStateReportRequest } from '../request-models/create-state-report-request.model';
import { SubmitStateReportRequest } from '../request-models/submit-state-report-request.model';
import { StateReportProgressEvent } from '../response-models/state-report-progress-event.model';
import { StateReportCompleteEvent } from '../response-models/state-report-complete-event.model';
import { StateReportFailedEvent } from '../response-models/state-report-failed-event.model';
import { Page } from '../response-models/page.model';
import { US_STATES } from '../../shared/constants/us-states';
import { STATE_REPORT_TYPE_VALUES, toCreateStateReportRequest } from '../utils/state-report-utils';
import { isRecord } from '../utils/is-record';
import { nullableString } from '../utils/wire-value-utils';
import { orgPrefix } from '../utils/org-path-utils';
import { withChanges } from '../utils/model-utils';

/** Query options for the state report list. */
export interface StateReportListOptions {
  page?: number;
  limit?: number;
  status?: StateReportStatus;
}

/**
 * State compliance reports: `/organizations/:orgId/state-reports[/:id[/generate|/submit]]`
 * (hub `user-state-reports-controller.ts`). The hub has no update or `/retry` route: a failed report is
 * retried by generating it again. Paged with `page` / `limit`.
 *
 * Holds the last loaded page as signal state and applies real-time progress from the WebSocket.
 * The `Observable` methods emit an `ApiError` on failure; the `Promise` helpers record the error's
 * message in `error` and resolve `null` / `false`.
 *
 * Reference: /agent-os/specs/2026-01-16-state-reports/spec.md
 */
@Injectable({
  providedIn: 'root'
})
export class StateReportService extends BaseService<StateReport> implements OnDestroy {
  private webSocketService = inject(WebSocketService);
  private destroy$ = new Subject<void>();

  // Signal-based state
  private reportsSignal = signal<StateReport[]>([]);
  private loadingSignal = signal<boolean>(false);
  private errorSignal = signal<string | null>(null);
  private totalCountSignal = signal<number>(0);
  private currentPageSignal = signal<number>(1);
  private pageSizeSignal = signal<number>(10);

  // Track WebSocket subscription state
  private isSubscribed = false;

  // Public computed signals
  readonly reports = computed(() => this.reportsSignal());
  readonly isLoading = computed(() => this.loadingSignal());
  readonly error = computed(() => this.errorSignal());
  readonly totalCount = computed(() => this.totalCountSignal());
  readonly currentPage = computed(() => this.currentPageSignal());
  readonly pageSize = computed(() => this.pageSizeSignal());

  // Report type options
  readonly reportTypes = STATE_REPORT_TYPE_VALUES;
  readonly usStates = US_STATES;

  // Computed stats
  readonly generatingReports = computed(() => this.reportsSignal().filter(r => r.status === 'generating'));
  readonly readyReports = computed(() => this.reportsSignal().filter(r => r.status === 'ready'));
  readonly failedReports = computed(() => this.reportsSignal().filter(r => r.status === 'failed'));

  constructor() {
    super('state-reports', StateReport);
    // Subscribe to WebSocket events when connection state changes to authenticated
    this.webSocketService.connectionState$.pipe(takeUntil(this.destroy$)).subscribe(state => {
      if (state === 'authenticated' && !this.isSubscribed) {
        this.setupWebSocketSubscriptions();
      } else if (state === 'disconnected') {
        this.isSubscribed = false;
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ============================================
  // API METHODS
  // ============================================

  /** One page of an organization's state reports. */
  listReports(orgId: string, options?: StateReportListOptions): Observable<Page<StateReport>> {
    return this.getPage({
      prefix: orgPrefix(orgId),
      query: { page: options?.page, limit: options?.limit, status: options?.status },
    });
  }

  /** Load a page of reports into signal state. Failures set `error`. */
  async loadReports(orgId: string, options?: StateReportListOptions): Promise<void> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);
    try {
      const page = await firstValueFrom(this.listReports(orgId, options));
      this.reportsSignal.set(page.items);
      this.totalCountSignal.set(page.paging.totalItems);
      this.currentPageSignal.set(page.paging.page);
      this.pageSizeSignal.set(page.paging.pageSize);
    } catch (error: unknown) {
      this.recordError(error, 'Failed to load reports');
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /** A single report, including its pre-signed `downloadUrl`. */
  getReport(orgId: string, reportId: string): Observable<StateReport | null> {
    return this.get(reportId, { prefix: orgPrefix(orgId) });
  }

  /** Fetch a single report; resolves `null` on failure. */
  async fetchReport(orgId: string, reportId: string): Promise<StateReport | null> {
    try {
      return await firstValueFrom(this.getReport(orgId, reportId));
    } catch (error: unknown) {
      console.error('Error fetching state report:', ApiError.fromHttpError(error).message);
      return null;
    }
  }

  /** Create a new state report in draft status. */
  createReport(orgId: string, data: CreateStateReportRequest): Observable<StateReport | null> {
    return this.create(toCreateStateReportRequest(data), { prefix: orgPrefix(orgId) });
  }

  /** Create a report and add it to signal state; resolves `null` on failure. */
  async createAndAddReport(orgId: string, data: CreateStateReportRequest): Promise<StateReport | null> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);
    try {
      const newReport = await firstValueFrom(this.createReport(orgId, data));
      if (newReport) {
        this.reportsSignal.update(reports => [newReport, ...reports]);
        this.totalCountSignal.update(count => count + 1);
      }
      return newReport;
    } catch (error: unknown) {
      this.recordError(error, 'Failed to create report');
      return null;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /** Delete (soft) a report. The hub refuses while it is generating. */
  deleteReport(orgId: string, reportId: string): Observable<void> {
    return this.delete(reportId, { prefix: orgPrefix(orgId) });
  }

  /** Delete a report and remove it from signal state; resolves `false` on failure. */
  async deleteAndRemoveReport(orgId: string, reportId: string): Promise<boolean> {
    try {
      await firstValueFrom(this.deleteReport(orgId, reportId));
      this.reportsSignal.update(reports => reports.filter(r => r.id !== reportId));
      this.totalCountSignal.update(count => Math.max(0, count - 1));
      return true;
    } catch (error: unknown) {
      this.recordError(error, 'Failed to delete report');
      return false;
    }
  }

  /** Queue PDF generation for a report. Emits the queued job's id. */
  generatePdf(orgId: string, reportId: string): Observable<{ jobId: string }> {
    return this.request('POST', { prefix: orgPrefix(orgId), id: reportId, path: 'generate' }, toJobResult, {});
  }

  /** Trigger PDF generation and mark the report generating; resolves `false` on failure. */
  async triggerPdfGeneration(orgId: string, reportId: string): Promise<boolean> {
    try {
      await firstValueFrom(this.generatePdf(orgId, reportId));
      this.patchReport(reportId, { status: 'generating', generationProgress: 0 });
      return true;
    } catch (error: unknown) {
      this.recordError(error, 'Failed to start generation');
      return false;
    }
  }

  /** Retry a failed report: the hub regenerates `failed` reports through `/generate`. Emits the queued job's id. */
  retryReport(orgId: string, reportId: string): Observable<{ jobId: string }> {
    return this.generatePdf(orgId, reportId);
  }

  /** Retry a failed report and mark it generating; resolves `false` on failure. */
  async retryFailedReport(orgId: string, reportId: string): Promise<boolean> {
    try {
      await firstValueFrom(this.retryReport(orgId, reportId));
      this.patchReport(reportId, { status: 'generating', generationProgress: 0, errorMessage: undefined });
      return true;
    } catch (error: unknown) {
      this.recordError(error, 'Failed to retry generation');
      return false;
    }
  }

  /** Record a report as submitted to `email` (ADN sends nothing). Emits the updated report. */
  submitReport(orgId: string, reportId: string, email: string): Observable<StateReport | null> {
    const body: SubmitStateReportRequest = { email };
    return this.create(body, { prefix: orgPrefix(orgId), id: reportId, path: 'submit' });
  }

  /** Submit a report and replace it in signal state; resolves `false` on failure. */
  async submitAndUpdateReport(orgId: string, reportId: string, email: string): Promise<boolean> {
    try {
      const updatedReport = await firstValueFrom(this.submitReport(orgId, reportId, email));
      if (updatedReport) {
        this.replaceReport(updatedReport);
      }
      return true;
    } catch (error: unknown) {
      this.recordError(error, 'Failed to submit report');
      return false;
    }
  }

  // ============================================
  // HELPER METHODS
  // ============================================

  /** Get report by ID from local state */
  getReportById(reportId: string): StateReport | undefined {
    return this.reportsSignal().find(r => r.id === reportId);
  }

  /** Get human-readable status text */
  getStatusText(status: StateReportStatus): string {
    switch (status) {
      case 'draft': return 'Draft';
      case 'generating': return 'Generating...';
      case 'ready': return 'Ready';
      case 'submitted': return 'Submitted';
      case 'failed': return 'Failed';
      default: return status;
    }
  }

  /** Get Bootstrap icon class for status */
  getStatusIcon(status: StateReportStatus): string {
    switch (status) {
      case 'draft': return 'bi-file-earmark';
      case 'generating': return 'bi-arrow-repeat';
      case 'ready': return 'bi-check-circle';
      case 'submitted': return 'bi-send-check';
      case 'failed': return 'bi-exclamation-triangle';
      default: return 'bi-file-earmark';
    }
  }

  /** Get report type label from value */
  getReportTypeLabel(value: string | null | undefined): string {
    return this.reportTypes.find(t => t.value === value)?.label || value || '';
  }

  /** Get state name from state code */
  getStateLabel(stateCode: string | null | undefined): string {
    return this.usStates.find(s => s.value === stateCode)?.label || stateCode || '';
  }

  /** Format date range for display */
  formatDateRange(startDate: string | null | undefined, endDate: string | null | undefined): string {
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };
    const format = (date: string | null | undefined) => (date ? new Date(date).toLocaleDateString('en-US', options) : '—');
    return `${format(startDate)} - ${format(endDate)}`;
  }

  /** Check if report can be deleted */
  canDelete(report: StateReport): boolean {
    return report.status !== 'generating';
  }

  /** Check if report can be retried */
  canRetry(report: StateReport): boolean {
    return report.status === 'failed';
  }

  /** Check if report can generate PDF */
  canGeneratePdf(report: StateReport): boolean {
    return report.status === 'draft';
  }

  /** Check if report can be submitted */
  canSubmit(report: StateReport): boolean {
    return report.status === 'ready';
  }

  /** Clear error state */
  clearError(): void {
    this.errorSignal.set(null);
  }

  // ============================================
  // WEBSOCKET SUBSCRIPTIONS
  // ============================================

  /** Listen for progress, complete and failed events once the socket is authenticated. */
  private setupWebSocketSubscriptions(): void {
    if (this.isSubscribed) return;
    this.isSubscribed = true;

    this.webSocketService.on<StateReportProgressEvent>('state-report:progress')
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => this.patchReport(event.reportId, { status: event.status, generationProgress: event.progress }));

    this.webSocketService.on<StateReportCompleteEvent>('state-report:complete')
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => this.patchReport(event.reportId, {
        status: event.status,
        reportUrl: event.url,
        recordCount: event.recordCount,
        fileSize: event.fileSize,
        generationProgress: 100,
      }));

    this.webSocketService.on<StateReportFailedEvent>('state-report:failed')
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => this.patchReport(event.reportId, { status: event.status, errorMessage: event.error, generationProgress: 0 }));
  }

  /** Replaces a report in state with a copy (same class) carrying `changes`. */
  private patchReport(reportId: string, changes: Partial<StateReport>): void {
    this.reportsSignal.update(reports =>
      reports.map(report => (report.id === reportId ? withChanges(report, changes) : report))
    );
  }

  private replaceReport(updated: StateReport): void {
    this.reportsSignal.update(reports => reports.map(r => (r.id === updated.id ? updated : r)));
  }

  private recordError(error: unknown, fallback: string): void {
    const apiError = ApiError.fromHttpError(error);
    console.error(`[StateReportService] ${fallback}:`, apiError.message);
    this.errorSignal.set(apiError.errors[0] ?? fallback);
  }
}



function toJobResult(data: unknown): { jobId: string } {
  return { jobId: (isRecord(data) ? nullableString(data['jobId']) : undefined) ?? '' };
}
